import type { Bet365SelectionData, BetMGMSelectionData, DraftKingsSelectionData, FanDuelSelectionData } from '../lib/api-client'
import type { Sportsbook } from '@/types/types'
import {
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { useState } from 'react'
import { useEvents } from '../hooks/useEvents'
import { globalBetSlipManager } from './GlobalBetSlipManager'
import { useUIPreferences } from './UIPreferencesManager'
import Widget from './Widget'

// Transformed selection data types (for bet slip - all strings)
type TransformedSelectionData
  = | { marketId: string, selectionId: string } // FanDuel
    | { selectionId: string } // DraftKings/Bet365
    | { optionId: string, marketId: string, fixtureId: string } // BetMGM

interface AppProps {
  instanceId: string
  league: string
}

function App({
  instanceId,
  league,
}: AppProps) {
  const { data: response, isPending, error } = useEvents(league)
  const { oddsFormat, toggleOddsFormat } = useUIPreferences()
  const [selectedMarketIndex, setSelectedMarketIndex] = useState<number>(0)

  const handleMarketChange = (marketIndex: number) => {
    setSelectedMarketIndex(marketIndex)
  }

  if (isPending)
    return <div>Loading...</div>
  if (error) {
    return (
      <div>
        Error:
        {error.message}
      </div>
    )
  }

  if (!response || !response.data || response.data.length === 0)
    return <div>No events available</div>

  const events = response.data
  const selectedEvent = events[selectedMarketIndex]

  const handleSelectionClick = (selectionIndex: number, sportsbook: Sportsbook) => {
    if (!selectedEvent?.selections?.length || !selectedEvent || !instanceId) {
      console.warn('Missing required data for bet selection')
      return
    }

    const selection = selectedEvent.selections[selectionIndex]
    if (!selection) {
      console.warn(`Selection at index ${selectionIndex} not found`)
      return
    }

    const oddsData = selection.odds[sportsbook]
    if (!oddsData) {
      console.warn(`No odds data found for ${sportsbook}`)
      return
    }

    // Convert selectedData to ensure string types for compatibility
    let selectedData: TransformedSelectionData

    // Handle different sportsbook data structures
    if (sportsbook === 'FanDuel' && oddsData.selectionData) {
      const fanDuelData = oddsData.selectionData as FanDuelSelectionData
      selectedData = {
        marketId: fanDuelData.marketId,
        selectionId: fanDuelData.selectionId.toString(),
      }
    }
    else if (sportsbook === 'DraftKings' && oddsData.selectionData) {
      const draftKingsData = oddsData.selectionData as DraftKingsSelectionData
      selectedData = {
        selectionId: draftKingsData.selectionId.toString(),
      }
    }
    else if (sportsbook === 'Bet365' && oddsData.selectionData) {
      const bet365Data = oddsData.selectionData as Bet365SelectionData
      selectedData = {
        selectionId: bet365Data.selectionId.toString(),
      }
    }
    else if (sportsbook === 'BetMGM' && oddsData.selectionData) {
      const betMGMData = oddsData.selectionData as BetMGMSelectionData
      selectedData = {
        optionId: betMGMData.optionId.toString(),
        marketId: betMGMData.marketId.toString(),
        fixtureId: betMGMData.fixtureId.toString(),
      }
    }
    else {
      // Default empty object if no selectionData available
      selectedData = { selectionId: '' }
    }

    const betData = {
      league,
      eventName: selectedEvent.name,
      marketName: selectedEvent.marketName,
      sportsbook,
      selectionName: selection.name,
      odds: {
        american: oddsData.american,
        decimal: oddsData.decimal,
      },
      selectedData,
      stake: '',
    }

    globalBetSlipManager.addBet(instanceId, betData)
  }

  return (
    <Widget
      leagueName={response.leagueName}
      markets={events}
      selectedMarketIndex={selectedMarketIndex}
      onMarketChange={handleMarketChange}
      oddsFormat={oddsFormat}
      onToggleOddsFormat={toggleOddsFormat}
      onSelectionClick={handleSelectionClick}
    />
  )
}

const queryClient = new QueryClient()

export default function AppWidget(props: AppProps) {
  return (
    <QueryClientProvider client={queryClient}>
      <App {...props} />
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  )
}
